import { api } from '@/utils/api';
import {
  IonButton,
  IonButtons,
  IonContent,
  IonHeader,
  IonIcon,
  IonItem,
  IonList,
  IonModal,
  IonSearchbar,
  IonTitle,
  IonToolbar,
} from '@ionic/react';
import { closeOutline, personAddOutline } from 'ionicons/icons';
import { Loader2 } from 'lucide-react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import FriendshipButton from './FriendshipButton';
import { ImageWithFallback } from './ui/fallbackImage';

type Friend = {
  id: number;
  username: string;
  firstName: string;
  lastName: string;
  imageUrl: string | null;
};

type FriendSearchProps = {
  onFriendSelect?: (friend: Friend) => void;
  customSearchCaption?: string;
};

export default function FriendSearch({ onFriendSelect, customSearchCaption }: FriendSearchProps) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [friends, setFriends] = useState<Friend[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [inputValue, setInputValue] = useState('');
  const [searchTouched, setSearchTouched] = useState(false);
  const debounceRef = useRef<NodeJS.Timeout | null>(null);

  // Open/close modal
  const handleOpenChange = (isOpen: boolean) => {
    setOpen(isOpen);
    if (!isOpen) {
      // Reset state when closing
      setInputValue('');
      setFriends([]);
      setSearchTouched(false);
    }
  };

  // Fetch friends from the API
  const fetchFriends = async (query: string) => {
    try {
      if (!query.trim()) return [];
      const { data } = await api.get(`/users/search?query=${encodeURIComponent(query.trim())}`);
      if (!data) throw new Error('No data received from API');
      return data;
    } catch (error) {
      console.error('Error fetching friends:', error);
      return [];
    }
  };

  // Handle search input and debounce API calls
  const handleSearch = useCallback(
    async (value: string) => {
      setIsSearching(true);
      setSearchTouched(true);

      if (value.trim()) {
        const data = await fetchFriends(value);
        setFriends(data);
      } else {
        setFriends([]);
      }

      setIsSearching(false);
    },
    []
  );

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      handleSearch(inputValue);
    }, 300);

    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [inputValue, handleSearch]);

  // Handle selecting a friend
  const handleSelectFriend = (friend: Friend) => {
    onFriendSelect?.(friend);
    setOpen(false);
    setSearchTouched(false);
    setFriends([]);
  };

  return (
    <>
      {/* Button to open modal */}
      <IonButton
        type="button"
        fill="clear"
        color="transparent"
        role="combobox"
        onClick={() => handleOpenChange(true)}
        className="text-foreground"
      >
        <IonIcon icon={personAddOutline} className="size-6" />
      </IonButton>

      {/* Modal for searching friends */}
      <IonModal isOpen={open} onDidDismiss={() => handleOpenChange(false)}>
        <IonHeader>
          <IonToolbar>
            <IonButtons slot="start">
              <IonButton color='medium' onClick={() => handleOpenChange(false)}><IonIcon icon={closeOutline} /></IonButton>
            </IonButtons>
            <IonTitle>{t('friends')}</IonTitle>
            <IonButtons slot="end">
              <IonButton onClick={() => handleOpenChange(false)}>{t('done')}</IonButton>
            </IonButtons>
          </IonToolbar>
          <IonToolbar>
            <IonSearchbar
              autoFocus
              className="p-0"
              autocomplete="off"
              placeholder={t('search_players')}
              onIonInput={(e) => setInputValue(e.detail.value || '')}
            />
          </IonToolbar>
        </IonHeader>

        <IonContent>
          <IonList className="flex-1 gap-4 space-y-2 overflow-y-auto">
            {/* Loading indicator */}
            {isSearching && (
              <div className="flex items-center justify-center py-6">
                <Loader2 className="size-6 animate-spin text-muted-foreground" />
              </div>
            )}

            {/* Friend list */}
            {!isSearching && friends.length > 0 && (
              friends.map((friend) => (
                <IonItem
                  key={friend.id}
                  onClick={() => handleSelectFriend(friend)}
                >
                  <ImageWithFallback
                    alt={`${friend.username} profile`}
                    fallbackImage="/assets/images/user.webp"
                    src={friend.imageUrl ? friend.imageUrl : '/assets/images/user.webp'}
                    className="size-12 shrink-0 overflow-hidden object-cover rounded-full mr-4"
                  />
                  <div>
                    <span className="font-semibold">{friend.firstName} {friend.lastName}</span>
                    <div className="text-sm text-gray-500">@{friend.username}</div>
                  </div>
                  <IonButtons slot="end">
                    <FriendshipButton dontCheckOnInit />
                  </IonButtons>
                </IonItem>
              ))
            )}

            {/* No results */}
            {!isSearching && friends.length === 0 && searchTouched && (
              <div className="flex flex-col items-center justify-center py-6">
                <img src="/assets/images/NoSearchResult.svg" alt="No results" />
                <h3 className="text-lg font-semibold">{t('no_results')}</h3>
              </div>
            )}
          </IonList>
        </IonContent>
      </IonModal>
    </>
  );
}